import { useTranslation } from "react-i18next";
import { BaseInsight, InsightsParams } from "./Insight.types";
import { filter, find, first, orderBy } from "lodash";
import { useDictionary } from "../content/useDictionary";
import { DimensionWithScores } from "@/features/people";
import { useMemo } from "react";
import {
  getInterviewFocusInsight,
  getCandidateLikelyStrengthInsight,
  getCandidateDevelopmentPotentialInsight,
  CandidateCompetencyInsightParams,
} from "./getCandidateCompetencyInsight";

export interface CandidateCompetencyInsight extends BaseInsight {
  type: "interview_focus" | "likely_strengths" | "development_potential";
}

export const useCandidateCompetencyInsights = (
  params: Omit<InsightsParams, "context">
) => {
  const { dimensions, person, scorecardRows } = params;

  const { t } = useTranslation();

  const { getAllTerms } = useDictionary();

  const sortedDims = orderBy(
    filter(dimensions, (dim) => dim.parentId !== null),
    (dim) => {
      const score = person
        ? find(dim.peopleScores, (ps) => ps.person?.id === person.id)
        : dim.score;
      return score?.value || 0;
    },
    "desc"
  );

  const highConfidenceDimensions = filter(
    sortedDims,
    (d) => d.score?.value && d.score.value > 65
  );
  const neutralDimensions = filter(
    sortedDims,
    (d) => d.score?.value && d.score.value > 35 && d.score.value <= 65
  );
  const lowConfidenceDimensions = filter(
    sortedDims,
    (d) => d.score?.value && d.score.value <= 35
  );

  const highConfidenceRows = filter(scorecardRows, (row) => {
    const dim = find(
      dimensions,
      (d) => d.id === first(row.dimensions as { dim_id: number }[])?.dim_id
    );
    return (
      dim &&
      dim.parentId !== null &&
      find(
        highConfidenceDimensions,
        (d: DimensionWithScores) => d.id === dim.parentId
      )
    );
  });

  const neutralRows = filter(scorecardRows, (row) => {
    const dim = find(
      dimensions,
      (d: DimensionWithScores) =>
        d.id === first(row.dimensions as { dim_id: number }[])?.dim_id
    );
    return (
      dim &&
      dim.parentId !== null &&
      find(neutralDimensions, (d: DimensionWithScores) => d.id === dim.parentId)
    );
  });

  const interviewFocusInsights = useMemo(() => {
    const lowConfidenceRowsDimensions = filter(
      lowConfidenceDimensions,
      (d: DimensionWithScores) => {
        const row = find(
          scorecardRows,
          (r) => d.id === first(r.dimensions as { dim_id: number }[])?.dim_id
        );
        return row && d.parentId !== null;
      }
    ) as DimensionWithScores[];

    return lowConfidenceRowsDimensions
      .map((d) => {
        const interviewQuestions = getAllTerms(d.id, "interview_question");
        const candidateParams: CandidateCompetencyInsightParams = {
          person,
          scorecardRows,
          defaultPersonId: params.defaultPersonId,
          interviewQuestions: interviewQuestions
            .map((q) => q?.text)
            .filter(Boolean) as string[],
        };
        return getInterviewFocusInsight(d, candidateParams, t);
      })
      .filter(
        (insight): insight is CandidateCompetencyInsight => insight !== null
      );
  }, [
    lowConfidenceDimensions,
    scorecardRows,
    person,
    params.defaultPersonId,
    t,
    getAllTerms,
  ]);

  const likelyStrengthsInsights = useMemo(() => {
    const highConfidenceRowsDimensions = filter(
      highConfidenceDimensions,
      (d: DimensionWithScores) => {
        const row = find(
          scorecardRows,
          (r) => d.id === first(r.dimensions as { dim_id: number }[])?.dim_id
        );
        return row && d.parentId !== null;
      }
    ) as DimensionWithScores[];

    const candidateParams: CandidateCompetencyInsightParams = {
      person,
      scorecardRows,
      defaultPersonId: params.defaultPersonId,
    };

    return highConfidenceRowsDimensions
      .map((d) => getCandidateLikelyStrengthInsight(d, candidateParams, t))
      .filter(
        (insight): insight is CandidateCompetencyInsight => insight !== null
      );
  }, [highConfidenceRows, scorecardRows, person, params.defaultPersonId, t]);

  const developmentPotentialInsights = useMemo(() => {
    const neutralRowsDimensions = filter(
      neutralDimensions,
      (d: DimensionWithScores) => {
        const row = find(
          scorecardRows,
          (r) => d.id === first(r.dimensions as { dim_id: number }[])?.dim_id
        );
        return row && d.parentId !== null;
      }
    ) as DimensionWithScores[];

    const candidateParams: CandidateCompetencyInsightParams = {
      person,
      scorecardRows,
      defaultPersonId: params.defaultPersonId,
    };

    return neutralRowsDimensions
      .map((d) =>
        getCandidateDevelopmentPotentialInsight(d, candidateParams, t)
      )
      .filter(
        (insight): insight is CandidateCompetencyInsight => insight !== null
      );
  }, [neutralRows, scorecardRows, person, params.defaultPersonId, t]);

  return {
    interviewFocus: interviewFocusInsights,
    likelyStrengths: likelyStrengthsInsights,
    developmentPotential: developmentPotentialInsights,
  };
};
